import React, { useMemo, useState } from "react";
import { Badge, Button, Col, DatePicker, Form, Input, Row, Select, Space, Tooltip } from "antd";
import { ReloadOutlined, SearchOutlined } from "@ant-design/icons";
import { AgGridReact } from "ag-grid-react";
import type { ColDef } from "ag-grid-community";
import { useIntl, type IntlShape } from "react-intl";
import dayjs, { Dayjs } from "dayjs";
import { useApolloClient } from "@apollo/client";
import { PartnerLookup } from "../../../../../components/PartnerLookup";
import * as Types from "../../../../../__generated__/types";
import {
    getSelfEntitlementResultByFilter_getSelfEntitlementResultByFilter_partners,
} from "../../../../../main/__generated__/getSelfEntitlementResultByFilter";
import { getRangePickerLocalization } from "../../../../info-portal/components/grid/CalendarUtils";
import { getAgGridLocalization } from "../../../../info-portal/components/grid/GridUtil";
import styles from "../../../../info-portal/components/styling/infoportal.module.css";
import { getI18n, isExternalUser } from "../../../../../utils/Utils";
import moduleStyles from "../../document.module.css";
import FreightGridToolbar from "../../components/FreightGridToolbar";
import { useFreightGridToolbar } from "../../hooks/useFreightGridToolbar";
import FreightWorkspaceShell from "../../components/FreightWorkspaceShell";
import { useListBorderosForCarrierLazyQuery } from "../queries.generated";
import { mapListBorderosForCarrierRows } from "../adapters/favorAdapters";
import { notifyFreightAuthOrGenericError } from "../freightFavorErrorHandling";
import { DOCUMENT_USER_ENTITLEMENT_RESULT_BY_USER_ID } from "../../../../../main/queries";
import {
    getDocumentUserEntitlementResultByUserId,
} from "../../../../../main/__generated__/getDocumentUserEntitlementResultByUserId";
import { ADDITIONAL_FREIGHT_TABS_ENTITLEMENT } from "../freightEntitlements";
import {
    getFreightDateBounds,
    getFreightDefaultDateRange,
    isFreightDateOutOfRange,
} from "../freightDateRange";

type DocumentRequestStatus = Types.BorderoEdpStatus;

type DocumentRequestRow = ReturnType<typeof mapListBorderosForCarrierRows>[number];

interface DocumentRequestFilter {
    partnerNo?: string;
    borderoNumber?: string;
    dateFrom?: Dayjs;
    dateUntil?: Dayjs;
    status?: DocumentRequestStatus[];
}

const STATUS_OPTIONS: DocumentRequestStatus[] = [
    Types.BorderoEdpStatus.DocumentRequested,
    Types.BorderoEdpStatus.DocumentReceived,
    Types.BorderoEdpStatus.InProgress,
    Types.BorderoEdpStatus.InCorrection,
    Types.BorderoEdpStatus.InSettlement,
    Types.BorderoEdpStatus.Billed,
    Types.BorderoEdpStatus.Ok,
];

interface DocumentRequestsProps {
    currentUser?: any;
    distinctEntitlements?: string[];
}

const onlyShowFreightPartners = (
    data: getSelfEntitlementResultByFilter_getSelfEntitlementResultByFilter_partners[],
) => Array.from(data.filter((item) => item?.category === "Z").values());

const filterRowsBySearch = (
    sourceRows: DocumentRequestRow[],
    filters: DocumentRequestFilter,
): DocumentRequestRow[] => {
    const borderoNumberFilter = filters.borderoNumber?.trim().toLowerCase();
    const selectedStatuses = filters.status ?? [];

    return sourceRows.filter((row) => {
        const matchesBorderoNumber = !borderoNumberFilter || row.borderoNumber.toLowerCase().includes(borderoNumberFilter);
        const matchesStatus = !selectedStatuses.length || selectedStatuses.includes(row.status);
        return matchesBorderoNumber && matchesStatus;
    });
};

function getStatusOptionLabel(status: DocumentRequestStatus, intl: IntlShape): string {
    switch (status) {
        case Types.BorderoEdpStatus.DocumentRequested:
            return getI18n("freight-status-document-requested", "Document Requested", intl);
        case Types.BorderoEdpStatus.DocumentReceived:
            return getI18n("freight-status-document-received", "Document Received", intl);
        case Types.BorderoEdpStatus.InProgress:
            return getI18n("freight-status-in-progress", "In Progress", intl);
        case Types.BorderoEdpStatus.InCorrection:
            return getI18n("freight-status-in-correction", "In Correction", intl);
        case Types.BorderoEdpStatus.InSettlement:
            return getI18n("freight-status-in-settlement", "In Settlement", intl);
        case Types.BorderoEdpStatus.Billed:
            return getI18n("freight-status-billed", "Billed", intl);
        case Types.BorderoEdpStatus.Ok:
            return getI18n("freight-status-ok", "OK", intl);
        default:
            return status;
    }
}

const BorderoStatus: React.FC<DocumentRequestsProps> = ({ currentUser, distinctEntitlements }) => {
    const intl = useIntl();
    const selectedLanguage = localStorage.getItem("locale") || "en";
    const { minDate, maxDate } = useMemo(() => getFreightDateBounds(), []);
    const isInternalUser = !isExternalUser(distinctEntitlements);
    const [form] = Form.useForm<DocumentRequestFilter>();
    const [rows, setRows] = useState<DocumentRequestRow[]>([]);
    const defaultDateRange = useMemo<DocumentRequestFilter>(() => getFreightDefaultDateRange(), []);
    const partnerRules = useMemo(
        () => (isInternalUser ? [{
            required: true,
            message: getI18n(
                "freight-partner-required-internal",
                "Please select a partner before searching.",
                intl,
            ),
        }] : undefined),
        [intl, isInternalUser],
    );
    const [loadBorderos, { loading }] = useListBorderosForCarrierLazyQuery({
        fetchPolicy: "network-only",
        context: {
            skip401Logout: true,
        },
    });
    const apolloClient = useApolloClient();

    const resolveCarrierNumberFilter = async (selectedPartnerNo?: string): Promise<string | undefined> => {
        const selectedCarrierNumber = selectedPartnerNo?.trim();

        if (selectedCarrierNumber) {
            return selectedCarrierNumber;
        }

        if (!isExternalUser(distinctEntitlements) || !currentUser?.uid) {
            return undefined;
        }

        try {
            const response = await apolloClient.query<getDocumentUserEntitlementResultByUserId>({
                query: DOCUMENT_USER_ENTITLEMENT_RESULT_BY_USER_ID,
                variables: {
                    userId: currentUser.uid,
                    entitlement: ADDITIONAL_FREIGHT_TABS_ENTITLEMENT,
                },
                fetchPolicy: "network-only",
            });
            const entitledPartnerNumbers = Array.from(
                new Set(
                    (response.data?.getDocumentUserEntitlementResultByUserId?.partners || [])
                        .map((partner) => partner?.partnerNo?.trim())
                        .filter((partnerNo): partnerNo is string => Boolean(partnerNo)),
                ),
            );

            return entitledPartnerNumbers.length ? entitledPartnerNumbers.join(",") : undefined;
        } catch (error) {
            console.error(error);
            return undefined;
        }
    };

    const renderStatusChip = (status: DocumentRequestStatus) => {
        const statusClassName = {
            [Types.BorderoEdpStatus.DocumentRequested]: moduleStyles.statusChipRequested,
            [Types.BorderoEdpStatus.DocumentReceived]: moduleStyles.statusChipRequested,
            [Types.BorderoEdpStatus.InProgress]: moduleStyles.statusChipProgress,
            [Types.BorderoEdpStatus.InSettlement]: moduleStyles.statusChipSettlement,
            [Types.BorderoEdpStatus.Billed]: moduleStyles.statusChipBilled,
            [Types.BorderoEdpStatus.Ok]: moduleStyles.statusChipRequested,
        }[status] || moduleStyles.statusChipRequested;

        return (
            <span className={`${moduleStyles.statusChip} ${statusClassName}`}>
                {getStatusOptionLabel(status, intl)}
            </span>
        );
    };

    const columnDefs = useMemo<ColDef<DocumentRequestRow>[]>(
        () => [
            {
                headerName: "",
                colId: "select",
                checkboxSelection: true,
                headerCheckboxSelection: true,
                width: 50,
                pinned: "left",
                suppressMenu: true,
                sortable: false,
                resizable: false,
            },
            {
                headerName: getI18n("freight-bordero-status-grid-status", "Status", intl),
                colId: "status",
                field: "status",
                minWidth: 170,
                cellRenderer: (params: { value: DocumentRequestStatus }) => renderStatusChip(params.value),
            },
            {
                headerName: getI18n("freight-bordero-status-grid-bordero-date", "Bordero Date", intl),
                colId: "borderoDate",
                field: "borderoDate",
                minWidth: 140,
            },
            {
                headerName: getI18n("freight-bordero-status-grid-bordero-number", "Bordero Number", intl),
                colId: "borderoNumber",
                field: "borderoNumber",
                minWidth: 160,
            },
            {
                headerName: getI18n("freight-bordero-status-grid-partner", "Partner", intl),
                colId: "carrierNumber",
                field: "carrierNumber",
                minWidth: 150,
            },
            {
                headerName: getI18n("freight-bordero-status-grid-order-area", "Order Area", intl),
                colId: "orderArea",
                field: "orderArea",
                minWidth: 130,
            },
            {
                headerName: getI18n("freight-bordero-status-grid-relation", "Relation", intl),
                colId: "relation",
                field: "relation",
                minWidth: 120,
            },
            {
                headerName: getI18n("freight-bordero-status-grid-position-count", "Position Count", intl),
                colId: "positionCount",
                field: "positionCount",
                minWidth: 140,
            },
            {
                headerName: getI18n("freight-bordero-status-grid-weight", "Weight", intl),
                colId: "weight",
                field: "weight",
                minWidth: 120,
            },
            {
                headerName: getI18n("freight-bordero-status-grid-fsd-number", "FSD Number", intl),
                colId: "fsdNumber",
                field: "fsdNumber",
                minWidth: 140,
            },
            {
                headerName: getI18n("freight-bordero-status-grid-goods-category", "Goods Category", intl),
                colId: "goodsCategory",
                field: "goodsCategory",
                minWidth: 150,
            },
        ],
        [intl],
    );

    const {
        menuItems,
        onExport,
        onGridReady,
        onResetView,
    } = useFreightGridToolbar<DocumentRequestRow>({
        columnDefs,
        exportWarningTitle: "Export",
        intl,
        storageKey: "freight-bordero-status_ag_grid_personalized_view",
    });

    const onSearch = async (values: DocumentRequestFilter) => {
        try {
            const carrierNumber = await resolveCarrierNumberFilter(values.partnerNo);
            const response = await loadBorderos({
                variables: {
                    request: {
                        carrierNumber,
                        borderoNumber: values.borderoNumber?.trim() || undefined,
                        dateFrom: values.dateFrom ? dayjs(values.dateFrom).format("YYYY-MM-DD") : undefined,
                        dateUntil: values.dateUntil ? dayjs(values.dateUntil).format("YYYY-MM-DD") : undefined,
                        status: values.status?.length ? values.status : undefined,
                    },
                },
            });

            const mappedRows = mapListBorderosForCarrierRows(response.data?.listBorderosForCarrier?.entity);
            const filteredRows = filterRowsBySearch(mappedRows, values);

            setRows(filteredRows);
        } catch (error) {
            console.error(error);
            notifyFreightAuthOrGenericError({
                error,
                intl,
                unauthorizedMessage: {
                    id: "freight-document-request-search-unauthorized",
                    defaultMessage: "You are not authorized to load bordero data.",
                },
                genericMessage: {
                    id: "freight-document-request-search-error",
                    defaultMessage: "Unable to load bordero data. Please try again.",
                },
                useToast: "message",
            });
        }
    };

    const onReset = () => {
        form.resetFields();
        setRows([]);
    };

    const handlePartnerChange = () => undefined;

    return (
        <FreightWorkspaceShell
            searchForm={
                <Form form={form} layout="vertical" onFinish={onSearch} initialValues={defaultDateRange}>
                    <Row gutter={16}>
                        <PartnerLookup
                            intl={intl}
                            entitlement={ADDITIONAL_FREIGHT_TABS_ENTITLEMENT}
                            onChange={handlePartnerChange}
                            currentUser={currentUser}
                            distinctEntitlements={distinctEntitlements}
                            filter={onlyShowFreightPartners}
                            colSpan={6}
                            rules={partnerRules}
                        />
                        <Col xs={24} md={6}>
                            <Form.Item
                                name="borderoNumber"
                                label={getI18n("freight-bordero-number", "Bordero Number", intl)}
                            >
                                <Input
                                    allowClear={true}
                                    placeholder={getI18n("freight-bordero-number-placeholder", "Enter Bordero Number", intl)}
                                />
                            </Form.Item>
                        </Col>
                        <Col xs={24} md={6}>
                            <Form.Item
                                name="dateFrom"
                                label={getI18n("freight-beleganforderung-valid-from", "Bordero Date From", intl)}
                                dependencies={["dateUntil"]}
                                rules={[
                                    ({ getFieldValue }) => ({
                                        validator(_, value?: Dayjs) {
                                            if (!value) {
                                                return Promise.resolve();
                                            }

                                            const normalizedValue = value.startOf("day");
                                            const dateUntil = getFieldValue("dateUntil") as Dayjs | undefined;

                                            if (normalizedValue.isBefore(minDate) || normalizedValue.isAfter(maxDate)) {
                                                return Promise.reject(new Error(getI18n(
                                                    "freight-date-from-range-error",
                                                    "Bordero Date From must be between today minus 1095 days and today.",
                                                    intl,
                                                )));
                                            }

                                            if (dateUntil && normalizedValue.isAfter(dateUntil.startOf("day"))) {
                                                return Promise.reject(new Error(getI18n(
                                                    "freight-date-from-after-until-error",
                                                    "Bordero Date From cannot be later than Bordero Date Until.",
                                                    intl,
                                                )));
                                            }

                                            return Promise.resolve();
                                        },
                                    }),
                                ]}
                            >
                                <DatePicker
                                    className={moduleStyles.fullWidthControl}
                                    format="YYYY-MM-DD"
                                    placeholder={getI18n("freight-beleganforderung-valid-from-placeholder", "yyyy-MM-dd", intl)}
                                    locale={getRangePickerLocalization(intl, selectedLanguage)}
                                    disabledDate={isFreightDateOutOfRange}
                                />
                            </Form.Item>
                        </Col>
                        <Col xs={24} md={6}>
                            <Form.Item
                                name="dateUntil"
                                label={getI18n("freight-beleganforderung-valid-until", "Bordero Date Until", intl)}
                                dependencies={["dateFrom"]}
                                rules={[
                                    ({getFieldValue}) => ({
                                        validator(_, value?: Dayjs) {
                                            if (!getFieldValue("dateFrom") || value) {
                                                const dateFrom = getFieldValue("dateFrom") as Dayjs | undefined;

                                                if (!value) {
                                                    return Promise.resolve();
                                                }

                                                const normalizedValue = value.startOf("day");

                                                if (normalizedValue.isBefore(minDate) || normalizedValue.isAfter(maxDate)) {
                                                    return Promise.reject(new Error(getI18n(
                                                        "freight-date-until-range-error",
                                                        "Bordero Date Until must be between today minus 1095 days and today.",
                                                        intl,
                                                    )));
                                                }

                                                if (dateFrom && normalizedValue.isBefore(dateFrom.startOf("day"))) {
                                                    return Promise.reject(new Error(getI18n(
                                                        "freight-date-until-before-from-error",
                                                        "Bordero Date Until cannot be earlier than Bordero Date From.",
                                                        intl,
                                                    )));
                                                }

                                                return Promise.resolve();
                                            }

                                            return Promise.reject(new Error(getI18n("freight-date-to-required", "Date until is required when Date from is selected", intl)));
                                        },
                                    }),
                                ]}
                            >
                                <DatePicker
                                    className={moduleStyles.fullWidthControl}
                                    format="YYYY-MM-DD"
                                    placeholder={getI18n("freight-beleganforderung-valid-until-placeholder", "yyyy-MM-dd", intl)}
                                    locale={getRangePickerLocalization(intl, selectedLanguage)}
                                    disabledDate={isFreightDateOutOfRange}
                                />
                            </Form.Item>
                        </Col>
                        <Col xs={24} md={6}>
                            <Form.Item
                                name="status"
                                label={getI18n("freight-document-request-status", "Status", intl)}
                            >
                                <Select
                                    allowClear={true}
                                    mode="multiple"
                                    placeholder={getI18n("freight-document-request-status-placeholder", "Type of status to be selected", intl)}
                                    options={STATUS_OPTIONS.map((status) => ({label: getStatusOptionLabel(status, intl), value: status}))}
                                />
                            </Form.Item>
                        </Col>
                    </Row>

                    <div className={moduleStyles.filterActions}>
                        <Tooltip title={intl.formatMessage({ id: "info-portal-search", defaultMessage: "Search" })}>
                            <Button
                                style={{ marginRight: "10px" }}
                                data-testid="info-portal-search-button"
                                type="primary"
                                htmlType="submit"
                                icon={<SearchOutlined />}
                                loading={loading}
                                size="large"
                            />
                        </Tooltip>

                        <Tooltip title={intl.formatMessage({ id: "do-clear", defaultMessage: "Clear" })}>
                            <Button
                                data-testid="info-portal-clear"
                                className={styles.simpleFlipRefresh}
                                type="primary"
                                icon={<ReloadOutlined />}
                                onClick={onReset}
                                size="large"
                            />
                        </Tooltip>
                    </div>
                </Form>
            }
            resultHeader={
                <div className={moduleStyles.freightResultMeta}>
                    <span className={moduleStyles.freightResultCount}>
                        {getI18n("freight-bordero-status-results", "Bordero Results", intl)}
                    </span>
                    <Space>
                        <Badge
                            className="site-badge-count-109"
                            count={rows.length}
                            style={{backgroundColor: "#52c41a"}}
                            title={"Number of Bordero Results"}
                            overflowCount={999999}
                        />
                    </Space>
                </div>
            }
            toolbar={
                <FreightGridToolbar
                    intl={intl}
                    menuItems={menuItems}
                    onExport={onExport}
                    onResetView={onResetView}
                />
            }
            grid={
                <div className={`ag-theme-balham ${moduleStyles.documentRequestGrid}`}>
                    <AgGridReact
                        defaultColDef={{
                            sortable: true,
                            resizable: true,
                            filter: true,
                        }}
                        columnDefs={columnDefs}
                        rowData={rows}
                        rowSelection="multiple"
                        suppressRowClickSelection={true}
                        pagination={true}
                        paginationPageSize={20}
                        localeText={getAgGridLocalization(intl)}
                        enableBrowserTooltips={true}
                        onGridReady={onGridReady}
                        data-testid="document-requests-grid"
                    />
                </div>
            }
        />
    );
};

export default BorderoStatus;
